import { useMemo } from 'react';

/**
 * FireflyField — 全局萤火虫氛围
 *
 * 设计规则：
 * - 不是装饰，是 Motion Language
 * - 数量：hero 8-15，普通页 4-8，移动端 2-5
 * - 动画：呼吸 + 极慢漂浮，周期 4-9s
 * - 视觉：中心 1-2px + soft glow 4-8px
 * - 颜色：rgba(244,223,162,.65)，大部分时间 opacity < 0.5
 * - 空间逻辑：不同页面定义不同区域
 */

// 生成确定性的"伪随机"避免 SSR hydration mismatch
function seededRandom(seed) {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

const FireflyField = ({
  count = 6,
  area = 'default', // 'hero' | 'default' | 'footer'
  className = '',
}) => {
  const fireflies = useMemo(() => {
    return Array.from({ length: count }, (_, i) => {
      const seed = i * 137.5 + area.length * 31;
      return {
        id: i,
        // 位置 (%)
        x: seededRandom(seed) * 100,
        y: seededRandom(seed + 1) * 100,
        // 大小
        size: 1.5 + seededRandom(seed + 2) * 1.5, // 1.5-3px
        // 动画时长 (4-9s)
        duration: 4 + seededRandom(seed + 3) * 5,
        // 延迟
        delay: seededRandom(seed + 4) * 8,
        // 初始透明度
        baseOpacity: 0.15 + seededRandom(seed + 5) * 0.35,
      };
    });
  }, [count, area]);

  // 区域约束（萤火虫集中在合适区域）
  const getAreaStyle = () => {
    switch (area) {
      case 'hero':
        // Hero：左侧少，右侧森林/湖边多
        return { top: '10%', left: '30%', width: '65%', height: '70%' };
      case 'footer':
        // Footer：逐渐减少，偏底部
        return { bottom: '0', left: '10%', width: '80%', height: '60%' };
      default:
        // 普通页面：边缘分布
        return { top: '5%', left: '5%', width: '90%', height: '80%' };
    }
  };

  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute overflow-hidden ${className}`}
      style={getAreaStyle()}
    >
      {fireflies.map((f) => (
        <div
          key={f.id}
          className="absolute"
          style={{
            left: `${f.x}%`,
            top: `${f.y}%`,
            width: `${f.size}px`,
            height: `${f.size}px`,
            borderRadius: '50%',
            background: `rgba(244, 223, 162, ${f.baseOpacity})`,
            boxShadow: `0 0 ${f.size * 2.5}px ${f.size}px rgba(244, 223, 162, ${f.baseOpacity * 0.6})`,
            animation: `
              fireflyBreath ${f.duration}s ease-in-out ${f.delay}s infinite,
              fireflyFloat ${f.duration * 1.6}s ease-in-out ${f.delay * 0.7}s infinite
            `,
            willChange: 'transform, opacity',
          }}
        />
      ))}
    </div>
  );
};

export default FireflyField;
